import ProductPreviousPurchase from "@flip/App/Components/Product/ProductPreviousPurchase";
import xProductPartNumbersStyles from "@flip/App/SharedStyle/ProductPartNumbersStyles";
import AngleRight from "@flip/XDL/Icons/AngleRight";
import { fontConfig } from "@flip/XDL/PreStyle/typography";
import { color, ellipsis, xScreen } from "@flip/XDL/Utilities";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import translate from "@insite/client-framework/Translate";
import { AutocompleteItemModel, ProductAutocompleteItemModel } from "@insite/client-framework/Types/ApiModels";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import { IconWrapper } from "@insite/mobius/Icon";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps, StyledTypography } from "@insite/mobius/Link";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React from "react";
import { css } from "styled-components";

interface Props {
    products: ProductAutocompleteItemModel[];
    focusedItem?: AutocompleteItemModel;
    goToUrl: (url: string) => void;
    extendedStyles?: AutocompleteProductsStyles;

    // XNG-Changes
    doSearch?: () => void;
}

// XNG-Changes: style
export interface AutocompleteProductsStyles {
    titleWrapper?: InjectableCss;
    seeMoreLink?: LinkPresentationProps;
    headerText?: TypographyPresentationProps;
    productsWrapper?: InjectableCss;
    container?: InjectableCss;
    focusedContainer?: InjectableCss;
    imageWrapper?: InjectableCss;
    image?: LazyImageProps;
    infoWrapper?: InjectableCss;
    partNumberWrapper?: InjectableCss;
    titleLink?: LinkPresentationProps;
    erpNumberText?: TypographyPresentationProps;
}

// XNG-Changes: style
export const autocompleteProductsStyles: AutocompleteProductsStyles = {
    titleWrapper: {
        css: css`
            display: flex;
            justify-content: flex-start;
            align-items: flex-end;
            flex-wrap: wrap;
            margin-bottom: 20px;
        `,
    },
    headerText: {
        variant: "h6",
        css: css`
            text-align: left;
            margin: 0 32px 0 0;
        `,
    },
    seeMoreLink: {
        mergeCss: true,
        icon: {
            iconProps: {
                src: AngleRight,
            },
            position: "right",
        },
        typographyProps: {
            size: 14,
            weight: fontConfig.weight.semiBold,
            css: css`
                letter-spacing: 0;
                line-height: 22px;
            `,
        },
        css: css`
            ${IconWrapper} {
                width: 12px;
                height: 12px;
                svg {
                    width: 12px;
                    height: 12px;
                }
            }
        `,
    },
    productsWrapper: {
        css: css`
            display: flex;
            justify-content: space-between;
            flex-wrap: wrap;
            column-gap: 43px;
            row-gap: 16px;
        `,
    },
    container: {
        css: css`
            display: flex;
            flex-wrap: nowrap;
            flex: 1 0 405px;
            cursor: pointer;
            ${xScreen("lte", "xs")} {
                flex: 1 0 100%;
            }
        `,
    },
    focusedContainer: {
        css: css`
            display: flex;
            flex-wrap: nowrap;
            flex: 1 0 410px;
            cursor: pointer;
        `,
    },
    imageWrapper: {
        css: css`
            margin-right: 12px;
            flex: 0 0 91px;
        `,
    },
    image: {
        width: "91px",
        height: "91px",
        css: css`
            margin-bottom: 8px;
            max-height: 100%;
            max-width: 100%;

            img {
                height: auto;
                max-height: 100%;
                max-width: 100%;
                width: auto;
            }
        `,
    },
    // XNG-Changes: FLIP-662
    infoWrapper: {
        css: css`
            display: flex;
            flex-direction: column;
            flex: 1;
            ${xScreen("lte", "xs")} {
                flex: 0 1 235px;
            }
        `,
    },
    titleLink: {
        typographyProps: {
            forwardAs: "h2",
            size: 14,
            lineHeight: "16px",
            weight: fontConfig.weight.bold,
            css: css`
                width: 100%;
                text-align: left;
                margin: 0;
                height: 50px;
                ${ellipsis(3)};

                &&${StyledTypography} {
                    color: ${color("text")};
                }
            `,
        },
        css: css`
            margin-bottom: 8px;
        `,
    },
    erpNumberText: {
        ellipsis: true,
        css: css`
            width: 270px;
            text-align: left;
        `,
    },
    partNumberWrapper: {
        css: css`
            margin-top: 3px;
        `,
    },
};

const styles = autocompleteProductsStyles;

class AutocompleteProducts extends React.Component<Props> {
    private readonly styles: AutocompleteProductsStyles;

    constructor(props: Props) {
        super(props);

        this.styles = mergeToNew(styles, props.extendedStyles);
    }

    render() {
        const { products } = this.props;
        const styles = this.styles;
        return (
            <>
                <StyledWrapper {...styles.titleWrapper}>
                    <Typography
                        {...styles.headerText}
                        data-test-selector="autocompleteProducts_Header"
                        className="autocomplete-products-header"
                    >
                        {translate("Products")}
                    </Typography>
                    <Link
                        {...styles.seeMoreLink}
                        onClick={this.props.doSearch}
                        data-test-selector="search-see-more"
                        aria-label="See More"
                    >
                        {translate("See More")}
                    </Link>
                </StyledWrapper>
                <StyledWrapper {...styles.productsWrapper}>
                    {products.map(product => (
                        <StyledWrapper
                            {...(this.props.focusedItem === product ? styles.focusedContainer : styles.container)}
                            key={product.id!}
                            onClick={() => {
                                this.props.goToUrl(product.url);
                            }}
                            className="autocomplete-products-container"
                        >
                            <StyledWrapper {...styles.imageWrapper} className="autocomplete-products-image-grid-item">
                                <LazyImage
                                    {...styles.image}
                                    src={product.image}
                                    className="autocomplete-products-image"
                                />
                                <ProductPreviousPurchase
                                    isPreviouslyPurchased={product.properties.isPreviouslyPurchased}
                                />
                            </StyledWrapper>
                            <StyledWrapper {...styles.infoWrapper} className="autocomplete-products-info-grid-item">
                                <Link
                                    {...styles.titleLink}
                                    data-test-selector={`productId_${product.id}`}
                                    className="autocomplete-products-link"
                                >
                                    {product.displayTitle}
                                </Link>
                                {/** XNG-Changes */}
                                {product.manufacturerItemNumber && (
                                    <Typography
                                        {...xProductPartNumbersStyles.default.value}
                                        data-test-selector="autocompleteProducts_PartNumber"
                                        className="autocomplete-products-erp-number"
                                    >
                                        <Typography {...xProductPartNumbersStyles.default.label}>
                                            {translate("MFR#")}
                                        </Typography>
                                        {/* XNG-Changes:FLIP-662 */}
                                        <Typography as="span" {...xProductPartNumbersStyles.default.value}>
                                            {product.manufacturerItemNumber}
                                        </Typography>
                                    </Typography>
                                )}
                                {product.properties.sku && (
                                    <StyledWrapper {...styles.partNumberWrapper}>
                                        <Typography
                                            {...xProductPartNumbersStyles.default.value}
                                            data-test-selector="autocompleteProducts_FlipPartNumber"
                                            className="autocomplete-products-fpn-number"
                                        >
                                            <Typography {...xProductPartNumbersStyles.default.label}>
                                                {translate("FPN#")}
                                            </Typography>
                                            {/* XNG-Changes:FLIP-662 */}
                                            <Typography as="span" {...xProductPartNumbersStyles.default.value}>
                                                {product.properties.sku}
                                            </Typography>
                                        </Typography>
                                    </StyledWrapper>
                                )}
                            </StyledWrapper>
                        </StyledWrapper>
                    ))}
                </StyledWrapper>
            </>
        );
    }
}

export default AutocompleteProducts;
